/**
 * Created by neo on 10.01.2026
 * Challenge Locations Component - Shows the route locations with progress
 */
import * as React from 'react';
import { useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { RouteChallengeLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeLocation';
import { RouteChallengeTeamReachedLocation } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeamReachedLocation';
import { Theme } from '../../../Theme/Theme';
import { MarkdownRenderer } from '../../../Components/Markdown/MarkdownRenderer';
import { getMediaSmallest } from '../../../Model/Media/Media';

const LocationsContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LocationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const LocationItem = styled.div<{ isReached: boolean; isFirst: boolean; isLast: boolean; isClickable: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  padding: 16px 0;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
  transition: background 0.2s;
  border-radius: 12px;
  margin: 0 -12px;
  padding-left: 12px;
  padding-right: 12px;

  &:hover {
    background: ${(props) => (props.isClickable ? Theme.Colors.lightestGrey : 'transparent')};
  }

  &::before {
    content: '';
    position: absolute;
    left: 31px;
    top: ${(props) => (props.isFirst ? '50%' : '0')};
    bottom: ${(props) => (props.isLast ? '50%' : '0')};
    width: 2px;
    background: ${(props) => (props.isReached ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  }
`;

const LocationMarker = styled.div<{ isReached: boolean; isStart: boolean; isEnd: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
  z-index: 1;
  background: ${(props) => {
    if (props.isStart) return Theme.Colors.success;
    if (props.isEnd) return Theme.Colors.error;
    if (props.isReached) return Theme.Colors.primary;
    return Theme.Colors.lightestGrey;
  }};
  color: ${(props) => (props.isReached || props.isStart || props.isEnd ? 'white' : Theme.Colors.charcoal)};
  border: 3px solid ${(props) => {
    if (props.isStart) return Theme.Colors.successDark;
    if (props.isEnd) return Theme.Colors.errorDark;
    if (props.isReached) return Theme.Colors.primaryDark;
    return Theme.Colors.lighterGrey;
  }};
`;

const LocationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const LocationName = styled.h3<{ isReached: boolean }>`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${(props) => (props.isReached ? Theme.Colors.textColor : Theme.Colors.charcoal)};
`;

const LocationDescriptionContainer = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
  margin: 0 0 8px 0;
  line-height: 1.4;

  p {
    margin: 0 0 4px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const LockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.charcoal};
  margin-left: 8px;
`;

const LocationPoints = styled.div<{ isReached: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => (props.isReached ? Theme.Colors.successOpacity : Theme.Colors.lightestGrey)};
  color: ${(props) => (props.isReached ? Theme.Colors.successDark : Theme.Colors.charcoal)};
`;

const ReachedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${Theme.Colors.primary};
  color: white;
`;

const LocationImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

// Extracted component to avoid hooks-in-loops
const LocationItemComponent: React.FC<{
  location: RouteChallengeLocation;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isReached: boolean;
  onLocationClick?: (location: RouteChallengeLocation, index: number) => void;
}> = observer(({ location, index, isFirst, isLast, isReached, onLocationClick }) => {
  const { t } = useTrans();
  const locationName = useLocalized(location.name);
  const locationDescription = useLocalized(location.description);
  const canShowContent = isReached;

  return (
    <LocationItem
      isReached={isReached}
      isFirst={isFirst}
      isLast={isLast}
      isClickable={!!onLocationClick}
      onClick={() => onLocationClick?.(location, index)}
    >
      <LocationMarker isReached={isReached} isStart={isFirst} isEnd={isLast}>
        {isFirst ? '🏁' : isLast ? '🎯' : index + 1}
      </LocationMarker>

      <LocationContent>
        <LocationName isReached={isReached}>
          {locationName || `Location ${index + 1}`}
          {isReached && (
            <ReachedBadge>
              ✓ {t('challenge.locations.reached', { defaultValue: 'Reached' })}
            </ReachedBadge>
          )}
          {!isReached && (
            <LockedBadge>
              🔒 {t('challenge.locations.locked', { defaultValue: 'Locked' })}
            </LockedBadge>
          )}
        </LocationName>
        {canShowContent && locationDescription && (
          <LocationDescriptionContainer>
            <MarkdownRenderer text={locationDescription} />
          </LocationDescriptionContainer>
        )}
        <LocationPoints isReached={isReached}>
          ⭐ {location.pointsRequired.toLocaleString()}{' '}
          {t('challenge.locations.points', { defaultValue: 'points' })}
        </LocationPoints>
      </LocationContent>

      {canShowContent && location.image && (
        <LocationImage
          src={getMediaSmallest(location.image)}
          alt={locationName}
        />
      )}
    </LocationItem>
  );
});

export type ChallengeLocationsProps = {
  locations: RouteChallengeLocation[];
  language: string;
  reachedLocations?: RouteChallengeTeamReachedLocation[];
  onLocationClick?: (location: RouteChallengeLocation, index: number) => void;
  challengeStarted?: boolean;
};

export const ChallengeLocations: React.FC<ChallengeLocationsProps> = observer(
  ({ locations, language, reachedLocations = [], onLocationClick, challengeStarted = false }) => {
    const { t } = useTrans();

    const reachedLocationIds = new Set(reachedLocations.map((r) => r.locationId));

    // First location is always considered "reached" after challenge starts
    const isLocationReached = (locationId: string, index: number): boolean => {
      if (index === 0 && challengeStarted) return true;
      return reachedLocationIds.has(locationId);
    };

    const handleLocationClick = useCallback(
      (location: RouteChallengeLocation, index: number) => {
        if (onLocationClick) {
          onLocationClick(location, index);
        }
      },
      [onLocationClick],
    );

    return (
      <LocationsContainer>
        <SectionTitle>
          <span>🗺️</span>
          {t('challenge.locations.title', { defaultValue: 'Route Locations' })}
        </SectionTitle>

        <LocationsList>
          {locations.map((location, index) => (
            <LocationItemComponent
              key={location.id}
              location={location}
              index={index}
              isFirst={index === 0}
              isLast={index === locations.length - 1}
              isReached={isLocationReached(location.id, index)}
              onLocationClick={handleLocationClick}
            />
          ))}
        </LocationsList>
      </LocationsContainer>
    );
  },
);
